import { LANDING_FALLBACK } from "../views/pages/welcome/landingContent";
import {
  ISO_CERTIFICATION_LOGO,
  LEGAL_DOCUMENT_URLS,
  findOptionalFooterDocument,
  resolveLegalDocumentUrl,
  toPublicAssetHref,
} from "./legalDocuments";
import { OFFICIAL_PAYMENT_LOGOS } from "./paymentLogos";
import { OFFICIAL_SOCIAL_LINKS } from "./socialLinks";

function mapFooterLink(link) {
  if (/política de premios/i.test(link.label)) return null;
  if (/canal de servicio/i.test(link.label || "")) return null;
  if (/tratamiento/i.test(String(link.label || ""))) {
    return {
      ...link,
      url: toPublicAssetHref(LEGAL_DOCUMENT_URLS.dataTreatmentPdf),
    };
  }
  // Documentos aún no publicados: se activan desde la config (url) o cuando
  // el PDF existe en /docs/ (ver OPTIONAL_FOOTER_DOCUMENTS).
  const optionalDoc = findOptionalFooterDocument(link.label);
  if (optionalDoc) {
    return { ...link, url: optionalDoc.url || "", optional_doc: optionalDoc.key };
  }
  if (!link.url) return { ...link, url: "" };
  const url = rewriteHelpUrl(resolveLegalDocumentUrl(link.url, link.label));
  return { ...link, url };
}

function rewriteHelpUrl(url) {
  if (!url) return url;
  const path = String(url).split("?")[0].replace(/\/$/, "") || "/";
  if (path === "/soporte") return "/ayuda";
  return url;
}

/** Sello ISO: solo se agrega cuando hay imagen configurada (hoy no). */
function withIsoLogo(logos) {
  if (!ISO_CERTIFICATION_LOGO.image_url) return logos;
  return [
    ...logos,
    {
      image_url: ISO_CERTIFICATION_LOGO.image_url,
      alt: ISO_CERTIFICATION_LOGO.alt,
    },
  ];
}

function normalizeTrustLogos(footer, tipo) {
  if (tipo === "payment") {
    return OFFICIAL_PAYMENT_LOGOS;
  }

  const fallback = LANDING_FALLBACK.footer_section.regulatory_logos || [];

  let logos = [];
  if (Array.isArray(footer.regulatory_logos) && footer.regulatory_logos.length > 0) {
    logos = footer.regulatory_logos;
  } else {
    const rows = footer.footer_logos || footer.logos;
    if (Array.isArray(rows)) {
      logos = rows
        .filter((row) => String(row.tipo || row.type || "").toLowerCase() === tipo)
        .map((row) => ({
          image_url: row.image_url || row.url,
          alt: row.alt || row.label || row.name || "",
        }))
        .filter((row) => row.image_url);
    }
  }

  return withIsoLogo(logos.length ? logos : fallback);
}

function mapFooterColumns(columns = []) {
  return columns.map((col) => ({
    ...col,
    links: col.links?.map(mapFooterLink).filter(Boolean).filter((l) => !HIDDEN_FOOTER_LINK.test(l.label || "")) ?? [],
  }));
}

const REQUIRED_FOOTER_LINKS = [
  {
    column: "Enlaces rápidos",
    label: "¿Quiénes somos?",
    url: "",
    match: /qui[eé]nes\s+somos/i,
  },
  {
    column: "Enlaces rápidos",
    label: "Preguntas frecuentes",
    url: "/ayuda",
    match: /preguntas\s+frecuentes/i,
  },
  {
    column: "Enlaces rápidos",
    label: "PQR's",
    url: "/ayuda",
    match: /pqr/i,
  },
  {
    column: "Enlaces rápidos",
    label: "Manual de Juego Responsable",
    url: "",
    match: /juego\s+responsable/i,
  },
  {
    column: "Enlaces rápidos",
    label: "Blog",
    url: "/blog",
    match: /^\s*blog\s*$/i,
  },
  {
    column: "Legal y privacidad",
    label: "Términos y condiciones",
    url: LEGAL_DOCUMENT_URLS.terms,
    match: /t[eé]rminos/i,
  },
  {
    column: "Legal y privacidad",
    label: "Política de tratamiento de datos",
    url: LEGAL_DOCUMENT_URLS.dataTreatmentPdf,
    match: /tratamiento/i,
  },
  {
    column: "Legal y privacidad",
    label: "Política de cookies",
    url: "",
    match: /cookies/i,
  },
];

/** "Certificación ISO" se quitó del footer (oct-2026): Gold aún no está certificada. Se
 * descarta aunque venga del contenido administrable. */
const HIDDEN_FOOTER_LINK = /certificaci[oó]n\s+iso|^\s*iso\s*$/i;

const DEFAULT_FOOTER_COLUMNS = [
  {
    title: "Enlaces rápidos",
    links: [
      { label: "¿Quiénes somos?", url: "" },
      { label: "Preguntas frecuentes", url: "/ayuda" },
      { label: "PQR's", url: "/ayuda" },
      { label: "Manual de Juego Responsable", url: "" },
      { label: "Blog", url: "/blog" },
      { label: "Jugar ahora", url: "/apuestas" },
    ],
  },
  {
    title: "Legal y privacidad",
    links: [
      {
        label: "Política de privacidad",
        url: LEGAL_DOCUMENT_URLS.privacy,
      },
      {
        label: "Términos y condiciones",
        url: LEGAL_DOCUMENT_URLS.terms,
      },
      {
        label: "Política de tratamiento de datos",
        url: LEGAL_DOCUMENT_URLS.dataTreatmentPdf,
      },
      {
        label: "Política de cookies",
        url: "",
      },
    ],
  },
];

function footerHasLink(columns, match) {
  return columns.some((col) =>
    col.links?.some((link) => match.test(String(link.label || "")))
  );
}

function ensureRequiredFooterLinks(columns) {
  const next = columns.map((col) => ({
    ...col,
    links: [...(col.links || [])],
  }));

  REQUIRED_FOOTER_LINKS.forEach((required) => {
    if (footerHasLink(next, required.match)) return;
    let column = next.find(
      (col) =>
        String(col.title || "").toLowerCase() === required.column.toLowerCase()
    );
    if (!column) {
      column = { title: required.column, links: [] };
      next.push(column);
    }
    const link = mapFooterLink({ label: required.label, url: required.url });
    if (link) column.links.push(link);
  });

  return next;
}

/** Footer por defecto compartido entre landing y app. */
export function getDefaultSiteFooter() {
  const fb = LANDING_FALLBACK.footer_section;
  return {
    ...fb,
    columns: ensureRequiredFooterLinks(mapFooterColumns(DEFAULT_FOOTER_COLUMNS)),
  };
}

export function normalizeSiteFooter(footer) {
  if (!footer) return getDefaultSiteFooter();
  return {
    ...footer,
    company_description:
      footer.company_description ||
      LANDING_FALLBACK.footer_section.company_description,
    columns: ensureRequiredFooterLinks(
      mapFooterColumns(
        footer.columns?.length ? footer.columns : DEFAULT_FOOTER_COLUMNS
      )
    ),
    payment_logos: normalizeTrustLogos(footer, "payment"),
    regulatory_logos: normalizeTrustLogos(footer, "regulatory"),
    social_links: OFFICIAL_SOCIAL_LINKS,
  };
}
